import { orderSourceLabel } from '@/utils/orderSource';
/**
 * MobileDesignList — M1 screen for the Design tab on mobile.
 *
 * Shows a My Orders / To Design segmented tab strip, horizontally
 * scrollable day pills, and an order list where each row has a photo
 * tile shortcut on the right edge.
 *
 * Photo tile is display-only here (Chunk 4). Camera capture is wired
 * in Chunk 5 via the onPhotoTap prop.
 */
import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { Camera, Check, ChevronDown, ChevronLeft, ChevronRight, UserPlus, UserMinus } from 'lucide-react';
import { format } from 'date-fns';
import { formatDateForTab } from '@/utils/dateUtils';
import StatusBadge from '@/components/StatusBadge';
import { useAuth } from '@/context/AuthContext';
import DesignerPickerSheet from '@/components/DesignerPickerSheet';

// ── Photo tile ────────────────────────────────────────────────────────────
function PhotoTile({ order, onTap }) {
  const hasPhoto = !!order.arrangement_photo;
  return (
    <div
      className="flex flex-col items-center gap-1 flex-shrink-0 cursor-pointer"
      onClick={(e) => { e.stopPropagation(); onTap?.(order); }}
      data-testid={`mobile-photo-tile-${order.id}`}
    >
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
        hasPhoto
          ? 'bg-emerald-50 border-2 border-emerald-400'
          : 'bg-stone-50 border-2 border-dashed border-stone-300'
      }`}>
        {hasPhoto
          ? <Check className="h-5 w-5 text-emerald-500" />
          : <Camera className="h-5 w-5 text-stone-400" />
        }
      </div>
      <span className="text-[9px] font-medium text-stone-400">
        {hasPhoto ? 'retake' : 'snap'}
      </span>
    </div>
  );
}

// ── Order row ────────────────────────────────────────────────────────────
function OrderRow({ order, onRowTap, onPhotoTap, onAssign, onManage, compact, pending }) {
  const num = String(order.internal_order_number || '').match(/-(\d+)$/);
  return (
    <div
      className={`flex items-center gap-3 px-4 border-b border-stone-100 ${compact ? 'py-2' : 'py-3'}`}
      data-testid={`mobile-order-row-${order.id}`}
    >
      <div
        className="w-12 flex-shrink-0 text-center cursor-pointer"
        onClick={() => onRowTap?.(order)}
        data-testid={`mobile-order-num-${order.id}`}
      >
        <span className={`font-mono font-semibold ${num ? 'text-base text-stone-600' : 'text-sm text-stone-300'}`}>
          {num ? num[1] : '—'}
        </span>
      </div>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onRowTap?.(order)}>
        <span className="block text-sm font-semibold text-stone-900 truncate">
          {order.recipient_name || 'Unknown'}
        </span>
        <div className="text-[11px] text-stone-400 truncate mt-0.5">
          {order.total_price != null && (
            <span className="text-green-700 font-semibold mr-1.5">
              ${order.total_price.toFixed(2)}
            </span>
          )}
          {order.product_description || orderSourceLabel(order, 'Manual')}
        </div>
      </div>
      {onAssign && (
        <button
          disabled={pending}
          onClick={(e) => { e.stopPropagation(); onAssign(order); }}
          className="flex flex-col items-center gap-1 flex-shrink-0"
          data-testid={`mobile-assign-btn-${order.id}`}
        >
          <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-stone-50 border-2 border-dashed border-violet-300">
            <UserPlus className="h-5 w-5 text-violet-400" />
          </div>
          <span className="text-[9px] font-medium text-violet-400">assign</span>
        </button>
      )}
      {onManage && (
        <button
          disabled={pending}
          onClick={(e) => { e.stopPropagation(); onManage(order); }}
          className="flex flex-col items-center gap-1 flex-shrink-0"
          data-testid={`mobile-manage-btn-${order.id}`}
        >
          <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-stone-50 border-2 border-dashed border-violet-300">
            <UserMinus className="h-5 w-5 text-violet-400" />
          </div>
          <span className="text-[9px] font-medium text-violet-400">manage</span>
        </button>
      )}
      <PhotoTile order={order} onTap={onPhotoTap} />
    </div>
  );
}

// ── Skeleton row ─────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-100">
      <div className="w-12 flex-shrink-0 flex justify-center">
        <div className="h-5 w-8 bg-stone-100 rounded animate-pulse" />
      </div>
      <div className="flex-1 space-y-2">
        <div className="h-3.5 bg-stone-100 rounded w-2/3 animate-pulse" />
        <div className="h-2.5 bg-stone-100 rounded w-1/2 animate-pulse" />
      </div>
      <div className="w-12 h-12 rounded-lg bg-stone-100 animate-pulse flex-shrink-0" />
    </div>
  );
}

// ── Designer index row ────────────────────────────────────────────────────
function DesignerIndexRow({ designer, isSelf, onTap }) {
  return (
    <div
      className="flex items-center gap-3 px-4 py-3.5 border-b border-stone-100 cursor-pointer active:bg-stone-50"
      onClick={() => onTap(designer)}
      data-testid={`designer-row-${designer.id}`}
    >
      <div className="flex-1 min-w-0">
        <span className="text-sm font-semibold text-stone-900">
          {designer.name || designer.username}
        </span>
        {isSelf && (
          <span className="ml-1.5 text-[10px] font-medium text-stone-400">(you)</span>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-sm font-semibold text-stone-700">{designer.orderCount}</span>
        <span className="text-xs text-stone-400">assigned</span>
        <ChevronRight className="h-4 w-4 text-stone-300 ml-1" />
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function MobileDesignList({
  myOrders,
  allOrders,
  doneOrders,
  dateTabs,
  selectedDate,
  isPastDate,
  loading,
  isAdmin,
  designers,
  onDateChange,
  onSelectOrder,
  onPhotoTap,
  onAssign,
  actionBlocked = () => false,
  directoryState = 'ready',
  readState = 'ready',
  offline = false,
  contextKey,
}) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [showDone, setShowDone] = useState(false);
  const [selectedDesigner, setSelectedDesigner] = useState(null);
  const [assigningOrder, setAssigningOrder] = useState(null);
  const [managingOrder, setManagingOrder] = useState(null);

  const opening = useRef(null);
  const request = useRef(null);
  const mounted = useRef(false);
  const context = useRef(contextKey); context.current = contextKey;
  const closePicker = () => { opening.current = null; setAssigningOrder(null); setManagingOrder(null); };
  const openPicker = (order, manage = false) => {
    if (offline || actionBlocked(order) || directoryState !== 'ready') return;
    opening.current = { order, context: context.current };
    setAssigningOrder(manage ? null : order); setManagingOrder(manage ? order : null);
  };
  useLayoutEffect(() => {
    mounted.current = true; opening.current = null;
    setAssigningOrder(null); setManagingOrder(null); setSelectedDesigner(null);
    return () => { mounted.current = false; opening.current = null; };
  }, [contextKey]);

  const toDesignOrders = allOrders.filter(o => o.assigned_designer_id == null);

  const designerList = useMemo(() => {
    const withCounts = designers.map(d => ({
      ...d,
      orderCount: allOrders.filter(o => o.assigned_designer_id === d.id).length,
      isSelf: d.id === user?.id,
    }));
    const self = withCounts.find(d => d.isSelf);
    const others = withCounts
      .filter(d => !d.isSelf)
      .sort((a, b) => (a.name || a.username).localeCompare(b.name || b.username));
    return self ? [self, ...others] : others;
  }, [designers, allOrders, user]);

  const drillDownOrders = useMemo(() =>
    selectedDesigner
      ? allOrders.filter(o => o.assigned_designer_id === selectedDesigner.id && o.status === 'accepted')
      : [],
    [selectedDesigner, allOrders]
  );

  const activeOrders = activeTab === 'my' ? myOrders : toDesignOrders;

  const myCount = readState === 'ready' ? myOrders.length : '—';
  const toDesignCount = readState === 'ready' ? toDesignOrders.length : '—';

  const handleTabChange = (key) => {
    closePicker(); setActiveTab(key);
    setSelectedDesigner(null);
  };

  const chooseDesigner = async designerId => {
    const attempt = opening.current;
    if (!attempt || request.current === attempt || offline || actionBlocked(attempt.order) || !onAssign) return;
    const current = () => mounted.current && opening.current === attempt && context.current === attempt.context;
    request.current = attempt;
    try {
      const result = await onAssign(attempt.order, designerId, current);
      if (current() && result?.state === 'accepted') closePicker();
    } finally {
      if (request.current === attempt) request.current = null;
    }
  };

  const tabs = isAdmin
    ? [
        { key: 'all',      label: `TO DESIGN (${toDesignCount})` },
        { key: 'assigned', label: 'ASSIGNED ORDERS' },
      ]
    : [
        { key: 'all', label: `TO DESIGN (${toDesignCount})` },
        { key: 'my',  label: `MY ORDERS (${myCount})` },
      ];

  return (
    <div className="flex flex-col h-full bg-white" data-testid="mobile-design-list">

      {/* Segmented tabs */}
      <div className="flex border-b border-stone-200 flex-shrink-0">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`flex-1 py-2.5 text-[11px] font-bold tracking-wide transition-colors ${
              activeTab === tab.key
                ? 'text-stone-900 border-b-2 border-stone-900 -mb-px'
                : 'text-stone-400'
            }`}
            data-testid={`mobile-tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Day pills */}
      <div className="flex gap-2 px-4 py-2.5 overflow-x-auto flex-shrink-0 border-b border-stone-100 scrollbar-none">
        {dateTabs.slice(0, 7).map((date) => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const isSelected = selectedDate
            && format(selectedDate, 'yyyy-MM-dd') === dateStr
            && !isPastDate;
          return (
            <button
              key={date.toISOString()}
              onClick={() => onDateChange(date, false)}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                isSelected
                  ? 'bg-stone-900 text-white'
                  : 'bg-stone-100 text-stone-500'
              }`}
              data-testid={`mobile-day-pill-${dateStr}`}
            >
              {formatDateForTab(date)}
            </button>
          );
        })}
      </div>

      {/* Order list */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'assigned' ? (
          selectedDesigner ? (
            <>
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-stone-200 bg-stone-50 sticky top-0">
                <button
                  onClick={() => setSelectedDesigner(null)}
                  className="flex items-center gap-1 text-sm font-medium text-stone-600 hover:text-stone-900"
                  data-testid="designer-drill-back"
                >
                  <ChevronLeft className="h-4 w-4" />
                  All Designers
                </button>
                <span className="text-sm font-semibold text-stone-900 ml-auto truncate max-w-[50%]">
                  {selectedDesigner.name || selectedDesigner.username}
                </span>
              </div>
              {drillDownOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-stone-300">
                  <Camera className="h-10 w-10 mb-3 opacity-40" />
                  <p className="text-sm font-medium text-stone-400">{readState === 'ready' ? 'No assigned orders' : 'Current assignments unavailable'}</p>
                </div>
              ) : (
                drillDownOrders.map(order => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    onRowTap={onSelectOrder}
                    onPhotoTap={onPhotoTap}
                    onManage={offline ? undefined : order => openPicker(order, true)}
                    pending={actionBlocked(order) || directoryState !== 'ready'}
                  />
                ))
              )}
            </>
          ) : designerList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-sm text-stone-400">{directoryState === 'ready' ? 'No designers found' : 'Designer directory unavailable'}</p>
            </div>
          ) : (
            designerList.map(d => (
              <DesignerIndexRow
                key={d.id}
                designer={d}
                isSelf={d.id === user?.id}
                onTap={setSelectedDesigner}
              />
            ))
          )
        ) : loading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : activeOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-stone-300">
            <Camera className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm font-medium text-stone-400">
              {readState !== 'ready' ? 'Current orders unavailable' : activeTab === 'my' ? 'No orders claimed yet' : 'Nothing left to design'}
            </p>
            {activeTab === 'my' && (
              <p className="text-xs mt-1 text-stone-300">Switch to To Design to claim one</p>
            )}
          </div>
        ) : (
          activeOrders.map(order => (
            <OrderRow
              key={order.id}
              order={order}
              onRowTap={onSelectOrder}
              onPhotoTap={onPhotoTap}
              onAssign={isAdmin && activeTab === 'all' && !offline ? order => openPicker(order) : undefined}
              pending={actionBlocked(order) || directoryState !== 'ready'}
            />
          ))
        )}

        {/* Done section */}
        {activeTab !== 'assigned' && doneOrders.length > 0 && (
          <div className="border-t border-stone-100 mt-1">
            <button
              onClick={() => setShowDone(v => !v)}
              className="flex items-center gap-1.5 w-full px-4 py-2.5 text-[11px] font-semibold text-stone-400 uppercase tracking-wider"
              data-testid="mobile-done-toggle"
            >
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showDone ? 'rotate-180' : ''}`} />
              Done ({doneOrders.length})
            </button>
            {showDone && doneOrders.map(order => (
              <OrderRow
                key={order.id}
                order={order}
                onRowTap={onSelectOrder}
                onPhotoTap={onPhotoTap}
                compact
              />
            ))}
          </div>
        )}
      </div>

      {assigningOrder && (
        <DesignerPickerSheet
          designers={designerList}
          onSelect={designer => chooseDesigner(designer.id)}
          disabled={offline || actionBlocked(assigningOrder) || directoryState !== 'ready'}
          onClose={closePicker}
        />
      )}
      {managingOrder && (
        <DesignerPickerSheet
          title="Reassign to…"
          designers={designerList}
          excludeId={selectedDesigner?.id}
          onSelect={designer => chooseDesigner(designer.id)}
          disabled={offline || actionBlocked(managingOrder) || directoryState !== 'ready'}
          onUnassign={() => chooseDesigner(null)}
          onClose={closePicker}
        />
      )}
    </div>
  );
}
